import type { IconType } from 'react-icons'
import { FaClipboardList, FaFlask, FaDraftingCompass, FaProjectDiagram, FaLayerGroup, FaCompass } from 'react-icons/fa'

// Visual treatment for a lesson's `kind`. "lesson" is the plain numbered default
// and has no entry here (callers fall back to the position number). Every other
// kind renders an icon + an overline label instead of a number, plus a tinted row
// background so it reads as a different kind of thing at a glance. Add a kind here
// to light it up across the UI. Flat brand colors only (no gradients, no shadows).
export interface LessonKindMeta {
  label: string
  Icon: IconType
  chipBg: string // icon chip background
  chipText: string // icon color
  labelText: string // overline label color
  rowBg: string // list-row background (base + hover), tints the whole row
}

const KINDS: Record<string, LessonKindMeta> = {
  cheatsheet: {
    label: 'Cheat sheet',
    Icon: FaClipboardList,
    chipBg: 'bg-brand-sky/10',
    chipText: 'text-brand-sky',
    labelText: 'text-brand-sky',
    rowBg: 'bg-brand-sky/[0.06] hover:bg-brand-sky/10',
  },
  'worked-design': {
    label: 'Worked design',
    Icon: FaProjectDiagram,
    chipBg: 'bg-brand-teal/10',
    chipText: 'text-brand-teal',
    labelText: 'text-brand-teal',
    rowBg: 'bg-brand-teal/[0.06] hover:bg-brand-teal/10',
  },
  project: {
    label: 'Project',
    Icon: FaDraftingCompass,
    chipBg: 'bg-brand-coral/10',
    chipText: 'text-brand-coral',
    labelText: 'text-brand-coral',
    rowBg: 'bg-brand-coral/[0.06] hover:bg-brand-coral/10',
  },
  'case-study': {
    label: 'Case study',
    Icon: FaLayerGroup,
    chipBg: 'bg-brand-burgundy/10',
    chipText: 'text-brand-burgundy',
    labelText: 'text-brand-burgundy',
    rowBg: 'bg-brand-burgundy/[0.06] hover:bg-brand-burgundy/10',
  },
  lab: {
    label: 'Lab',
    Icon: FaFlask,
    chipBg: 'bg-brand-forest/10',
    chipText: 'text-brand-forest',
    labelText: 'text-brand-forest',
    rowBg: 'bg-brand-forest/[0.06] hover:bg-brand-forest/10',
  },
  'field-guide': {
    label: 'Field guide',
    Icon: FaCompass,
    chipBg: 'bg-brand-gold/10',
    chipText: 'text-brand-gold-deep',
    labelText: 'text-brand-gold-deep',
    rowBg: 'bg-brand-gold/[0.06] hover:bg-brand-gold/10',
  },
}

// Returns the visual treatment for a non-default kind, or null for a plain lesson.
export function lessonKindMeta(kind?: string): LessonKindMeta | null {
  if (!kind || kind === 'lesson') return null
  return KINDS[kind] ?? null
}

// The stored title may carry a redundant prefix ("Cheat sheet: Data Stores")
// so it still reads clearly without the badge. Strip it when we show the label.
export function lessonDisplayTitle(title: string, meta: LessonKindMeta | null): string {
  if (!meta) return title
  return title.replace(new RegExp(`^${meta.label}\\s*[:–-]\\s*`, 'i'), '')
}
